/**
 * Icon: the one icon set for Cockatrice (cockatrice-ui `src/ui/Icon.jsx`, ported to hyperscript; PLAN.md §0).
 * Line icons on a 24px grid, 1.8px stroke, round caps and joins, drawn in currentColor.
 * Decorative by default (aria-hidden). Give icon-only buttons an aria-label instead.
 *
 * Each entry is a function so every Icon renders its own SVG nodes.
 */
import './css/icon.css';
import { el } from './dom.ts';
import type { Attributes, Child } from './dom.ts';

/** One shape of an icon, in the SVG namespace (hyperscript makes `path`, `circle` and `rect` as SVG). */
const s = (tag: 'path' | 'circle' | 'rect', attributes: Attributes): Child => el(tag, attributes);

export const icons = {
  chevronUp: () => [s('path', { d: 'M6 15l6-6 6 6' })],
  menu: () => [s('path', { d: 'M4 7h16M4 12h16M4 17h16' })],
  sun: () => [s('circle', { cx: '12', cy: '12', r: '4' }), s('path', { d: 'M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4' })],
  minus: () => [s('path', { d: 'M5 12h14' })],
  external: () => [s('path', { d: 'M14 4h6v6' }), s('path', { d: 'M20 4l-9 9' }), s('path', { d: 'M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5' })],
  calendar: () => [s('rect', { x: '3', y: '5', width: '18', height: '16', rx: '2' }), s('path', { d: 'M3 10h18M8 3v4M16 3v4' })],
  mail: () => [s('rect', { x: '3', y: '5', width: '18', height: '14', rx: '2' }), s('path', { d: 'M3 7l9 6 9-6' })],
  user: () => [s('circle', { cx: '12', cy: '8', r: '4' }), s('path', { d: 'M4 21c0-4 4-6 8-6s8 2 8 6' })],
  grip: () => [s('circle', { cx: '9', cy: '6', r: '1', fill: 'currentColor' }), s('circle', { cx: '15', cy: '6', r: '1', fill: 'currentColor' }), s('circle', { cx: '9', cy: '12', r: '1', fill: 'currentColor' }), s('circle', { cx: '15', cy: '12', r: '1', fill: 'currentColor' }), s('circle', { cx: '9', cy: '18', r: '1', fill: 'currentColor' }), s('circle', { cx: '15', cy: '18', r: '1', fill: 'currentColor' })],
  sort: () => [s('path', { d: 'M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4' })],
  eyeOff: () => [s('path', { d: 'M3 3l18 18' }), s('path', { d: 'M10.6 6.1A10 10 0 0 1 12 6c6 0 10 6 10 6a17 17 0 0 1-3.3 3.8M6.6 6.6A17 17 0 0 0 2 12s4 6 10 6a10 10 0 0 0 4.4-1' }), s('path', { d: 'M9.9 9.9a3 3 0 0 0 4.2 4.2' })],
  refresh: () => [s('path', { d: 'M20 11a8 8 0 0 0-14.7-4.4L3 9' }), s('path', { d: 'M3 4v5h5' }), s('path', { d: 'M4 13a8 8 0 0 0 14.7 4.4L21 15' }), s('path', { d: 'M21 20v-5h-5' })],
  logout: () => [s('path', { d: 'M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4' }), s('path', { d: 'M10 17l-5-5 5-5M5 12h11' })],
  filter: () => [s('path', { d: 'M4 5h16l-6 8v5l-4 2v-7z' })],
  back: () => [s('path', { d: 'M15 18l-6-6 6-6' })],
  check: () => [s('path', { d: 'M5 12l5 5L20 7' })],
  chevronDown: () => [s('path', { d: 'M6 9l6 6 6-6' })],
  chevronLeft: () => [s('path', { d: 'M15 18l-6-6 6-6' })],
  chevronRight: () => [s('path', { d: 'M9 6l6 6-6 6' })],
  close: () => [s('path', { d: 'M6 6l12 12M18 6L6 18' })],
  comment: () => [s('path', { d: 'M4 5h16v11H9l-5 4z' })],
  copy: () => [s('rect', { x: '8', y: '8', width: '12', height: '12', rx: '2' }), s('path', { d: 'M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2' })],
  devices: () => [s('rect', { x: '2', y: '4', width: '14', height: '10', rx: '1.5' }), s('path', { d: 'M6 18h6' }), s('rect', { x: '17', y: '8', width: '5', height: '12', rx: '1' })],
  download: () => [s('path', { d: 'M12 4v11M7 10l5 5 5-5' }), s('path', { d: 'M5 20h14' })],
  edit: () => [s('path', { d: 'M4 20h4L19 9l-4-4L4 16z' })],
  eye: () => [s('path', { d: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z' }), s('circle', { cx: '12', cy: '12', r: '3' })],
  fingerprint: () => [s('path', { d: 'M12 11v3a6 6 0 0 1-1.5 4' }), s('path', { d: 'M8 11a4 4 0 0 1 8 0v2' }), s('path', { d: 'M5 12a7 7 0 0 1 14 0' }), s('path', { d: 'M16 16a9 9 0 0 1-1 3' })],
  folder: () => [s('path', { d: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' })],
  history: () => [s('path', { d: 'M3 12a9 9 0 1 0 3-6.7L3 8' }), s('path', { d: 'M3 3v5h5' }), s('path', { d: 'M12 7v5l3 2' })],
  info: () => [s('circle', { cx: '12', cy: '12', r: '9' }), s('path', { d: 'M12 11v6M12 7v.01' })],
  key: () => [s('circle', { cx: '8', cy: '15', r: '4' }), s('path', { d: 'M11 12l9-9M17 6l3 3M15 8l2 2' })],
  left: () => [s('path', { d: 'M15 18l-6-6 6-6' })],
  link: () => [s('path', { d: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1' }), s('path', { d: 'M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1' })],
  lock: () => [s('rect', { x: '5', y: '11', width: '14', height: '10', rx: '2' }), s('path', { d: 'M8 11V7a4 4 0 0 1 8 0v4' })],
  merge: () => [s('path', { d: 'M7 4v6a5 5 0 0 0 5 5h0a5 5 0 0 1 5 5v0M7 20V14M17 4v4' })],
  moon: () => [s('path', { d: 'M20 14.5A8 8 0 1 1 9.5 4 6.5 6.5 0 0 0 20 14.5z' })],
  more: () => [s('circle', { cx: '5', cy: '12', r: '1.2', fill: 'currentColor' }), s('circle', { cx: '12', cy: '12', r: '1.2', fill: 'currentColor' }), s('circle', { cx: '19', cy: '12', r: '1.2', fill: 'currentColor' })],
  notes: () => [s('path', { d: 'M5 3h10l4 4v14H5z' }), s('path', { d: 'M9 11h6M9 15h6' })],
  panel: () => [s('rect', { x: '3', y: '4', width: '18', height: '16', rx: '2' }), s('path', { d: 'M9 4v16' })],
  people: () => [s('circle', { cx: '9', cy: '8', r: '3' }), s('circle', { cx: '17', cy: '9', r: '2.5' }), s('path', { d: 'M3 20c0-3 3-5 6-5s6 2 6 5M15 15c3 0 6 1.5 6 4.5' })],
  phone: () => [s('rect', { x: '7', y: '2', width: '10', height: '20', rx: '2' }), s('path', { d: 'M11 18h2' })],
  photo: () => [s('rect', { x: '3', y: '4', width: '18', height: '16', rx: '2' }), s('circle', { cx: '9', cy: '10', r: '2' }), s('path', { d: 'M21 16l-5-5-8 9' })],
  picture: () => [s('rect', { x: '3', y: '4', width: '18', height: '16', rx: '2' }), s('circle', { cx: '9', cy: '10', r: '2' }), s('path', { d: 'M21 16l-5-5-8 9' })],
  play: () => [s('path', { d: 'M7 4l13 8-13 8z', fill: 'currentColor', stroke: 'none' })],
  plus: () => [s('path', { d: 'M12 5v14M5 12h14' })],
  plusSquare: () => [s('rect', { x: '4', y: '4', width: '16', height: '16', rx: '3' }), s('path', { d: 'M12 8v8M8 12h8' })],
  print: () => [s('path', { d: 'M7 9V3h10v6' }), s('rect', { x: '3', y: '9', width: '18', height: '8', rx: '2' }), s('path', { d: 'M7 14h10v7H7z' })],
  restore: () => [s('path', { d: 'M3 12a9 9 0 1 0 3-6.7L3 8' }), s('path', { d: 'M3 3v5h5' })],
  right: () => [s('path', { d: 'M9 6l6 6-6 6' })],
  search: () => [s('circle', { cx: '11', cy: '11', r: '7' }), s('path', { d: 'M20 20l-4-4' })],
  settings: () => [s('circle', { cx: '12', cy: '12', r: '3' }), s('path', { d: 'M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1L7 17M17 7l2.1-2.1' })],
  share: () => [s('path', { d: 'M12 3v12M7 8l5-5 5 5' }), s('path', { d: 'M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6' })],
  split: () => [s('path', { d: 'M4 12h16M12 4v4M12 16v4' })],
  trash: () => [s('path', { d: 'M4 7h16M10 11v6M14 11v6' }), s('path', { d: 'M6 7l1 13h10l1-13M9 7V4h6v3' })],
  upload: () => [s('path', { d: 'M12 15V3M7 8l5-5 5 5' }), s('path', { d: 'M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4' })],
  warn: () => [s('path', { d: 'M12 3l10 18H2z' }), s('path', { d: 'M12 10v5M12 18v.01' })],
} as const satisfies Readonly<Record<string, () => Child>>;

/** The name of an icon in the set. */
export type IconName = keyof typeof icons;

export interface IconProps {
  /** Which icon. */
  readonly name: IconName;
  /** 16 / 18 / 24 px. Defaults to md (18): toolbars; sm (16) inside buttons. */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** Makes the icon meaningful (role="img" with this name); leave it out when the words beside it say the same. */
  readonly label?: string | undefined;
  /** Extra class on the svg. */
  readonly class?: string | undefined;
}

const PIXELS = { sm: 16, md: 18, lg: 24 } as const;

export default function Icon(props: IconProps): Child {
  const px = (): number => PIXELS[props.size ?? 'md'];
  return el(
    'svg',
    {
      class: () => (props.class === undefined || props.class === '' ? 'ui-icon' : `ui-icon ${props.class}`),
      width: px,
      height: px,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.8',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      role: () => (props.label === undefined || props.label === '' ? undefined : 'img'),
      'aria-label': () => props.label,
      'aria-hidden': () => (props.label === undefined || props.label === '' ? 'true' : undefined),
      focusable: 'false',
    },
    () => icons[props.name](),
  );
}
